import { useUpdateAdminLocation } from '@hakimo-ui/hakimo/data-access';
import { AdminTenantLocation, LocationFormData } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { Button } from '@hakimo-ui/shared/ui-base';
import { toast } from '@hakimo-ui/hakimo/util';
import { useMemo, useState } from 'react';
import { LocationFormCard } from '../tenant-details/cards/LocationFormCard';

interface Props {
  tenantId: string;
  location: AdminTenantLocation;
  onCancel: () => void;
  onSuccess: () => void;
}

export function EditLocation(props: Props) {
  const { tenantId, location, onCancel, onSuccess } = props;
  const [formData, setFormData] = useState<LocationFormData>({
    name: location.name,
    street: location.address || '',
    city: location.city,
    state: location.state,
    country: location.country,
    timezone: location.timezone,
  });

  const onUpdateSuccess = () => {
    toast('Location updated successfully', { type: 'success' });
    onSuccess();
  };

  const onUpdateError = (error: Error) => {
    toast(error.message || 'Failed to update location', { type: 'error' });
  };

  const updateMutation = useUpdateAdminLocation(
    tenantId,
    location.id,
    onUpdateSuccess,
    onUpdateError
  );

  const handleClickSave = () => {
    if (!formData.name.trim()) {
      toast('Location name is required', { type: 'error' });
      return;
    }
    if (!formData.city.trim()) {
      toast('City is required', { type: 'error' });
      return;
    }
    if (!formData.state.trim()) {
      toast('State is required', { type: 'error' });
      return;
    }
    if (!formData.country.trim()) {
      toast('Country is required', { type: 'error' });
      return;
    }
    if (!formData.timezone.trim()) {
      toast('Timezone is required', { type: 'error' });
      return;
    }

    updateMutation.mutate({
      name: formData.name,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      timezone: formData.timezone,
      description: formData.street,
    });
  };

  const isSaveDisabled =
    !formData.name.trim() ||
    !formData.city.trim() ||
    !formData.state.trim() ||
    !formData.country.trim() ||
    !formData.timezone.trim() ||
    updateMutation.isLoading;

  const pageActions = useMemo(
    () => [
      <Button
        key="save"
        variant="primary"
        onClick={handleClickSave}
        disabled={isSaveDisabled}
        loading={updateMutation.isLoading}
      >
        Save
      </Button>,
      <Button
        key="cancel"
        onClick={onCancel}
        disabled={updateMutation.isLoading}
      >
        Cancel
      </Button>,
    ],
    [handleClickSave, isSaveDisabled, updateMutation.isLoading, onCancel]
  );

  return (
    <Page
      title="Edit"
      subtitle="Location"
      onClickBack={onCancel}
      pageActions={pageActions}
    >
      <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg h-full px-4 py-8">
        <div className="mx-auto w-full max-w-6xl">
          <LocationFormCard formData={formData} onChange={setFormData} />
        </div>
      </div>
    </Page>
  );
}

export default EditLocation;
